import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { ArrowDownTrayIcon, Bars3Icon, ChevronDownIcon, ChevronUpDownIcon, ChevronUpIcon, MagnifyingGlassIcon, Squares2X2Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { cx } from "../../utils/cx";
import Button from "./Button";
import { Select } from "./Field";
import EmptyState from "./EmptyState";
import Pagination from "./Pagination";
import { InCard } from "./RowAction";
import { Loading } from "./Spinner";

const cellValue = (row, column) => (column.accessor ? column.accessor(row) : row?.[column.key]);

const matches = (row, columns, query) =>
  columns.some((c) => String(cellValue(row, c) ?? "").toLowerCase().includes(query));

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

// Plain value a column sorts on: its sortValue, else what it shows
const sortValueOf = (row, column) => (column.sortValue ? column.sortValue(row) : cellValue(row, column));

// Numbers, short numeric strings, dates and ISO date strings compare as numbers; everything else as text. Long digit
// strings (national IDs, phones) stay text, which the numeric collator still orders correctly
const toComparable = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number" || typeof value === "boolean") return Number(value);
  const text = String(value).trim();
  if (/^-?\d{1,12}(\.\d+)?$/.test(text)) return Number(text);
  return /^\d{4}-\d{2}-\d{2}/.test(text) && !Number.isNaN(Date.parse(text)) ? Date.parse(text) : text;
};

const compare = (a, b) => (typeof a === "number" && typeof b === "number" ? a - b : collator.compare(String(a), String(b)));

// Labelled data columns sort unless they opt out; the row number and action columns never do
const isSortable = (column) =>
  column.sortable ?? (column.exportable !== false && typeof column.label === "string" && column.label.trim() !== "" && column.label !== "#");

const filterValueOf = (row, filter) => {
  const value = filter.value ? filter.value(row) : cellValue(row, filter);
  return value === null || value === undefined || value === "" ? null : String(value);
};

const nextDirection = { asc: "desc", desc: null };

const VIEWS = [
  { value: "table", label: "List view", Icon: Bars3Icon },
  { value: "cards", label: "Card view", Icon: Squares2X2Icon },
];

const isLabelled = (column) => typeof column.label === "string" && column.label.trim() !== "" && column.label !== "#";

/**
 * A row as a card, drawn from the columns: the first labelled one as the title, a state or status
 * column as its badge, the other labelled columns as label and value, and the unlabelled ones (the
 * row's actions, whose RowActions show their labels there) at the bottom.
 */
const cardFromColumns = (columns, onRowClick) => {
  const title = columns.find(isLabelled);
  const badge = columns.find((c) => c !== title && ["state", "status"].includes(c.key));
  const details = columns.filter((c) => c !== title && c !== badge && isLabelled(c));
  const actions = columns.filter((c) => !isLabelled(c) && c.label !== "#");
  const draw = (c, row, position) => (c.render ? c.render(row, position) : cellValue(row, c));
  return (row, position) => (
    <article
      onClick={onRowClick ? (e) => !e.target.closest("button, a, input, select, label") && onRowClick(row) : undefined}
      className={cx("flex h-full flex-col gap-4 rounded-lg border border-slate-100 bg-white p-5 shadow-sm", onRowClick && "cursor-pointer hover:border-brand/30")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 break-words font-medium text-slate-800">{title && draw(title, row, position)}</div>
        {badge && <div className="shrink-0">{draw(badge, row, position)}</div>}
      </div>
      {details.length > 0 && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          {details.map((c) => (
            <div key={c.key} className="min-w-0">
              <dt className="text-xs text-slate-500">{c.label}</dt>
              <dd className="break-words text-slate-700">{draw(c, row, position) ?? "—"}</dd>
            </div>
          ))}
        </dl>
      )}
      {actions.length > 0 && (
        <div className="mt-auto flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
          {actions.map((c) => (
            <div key={c.key}>{c.render?.(row, position)}</div>
          ))}
        </div>
      )}
    </article>
  );
};

// The view a reader chose last for this table, kept in the browser only
const savedView = (viewKey) => {
  try {
    return localStorage.getItem(`table-view:${viewKey}`) === "cards" ? "cards" : "table";
  } catch {
    return "table";
  }
};

const saveView = (viewKey, view) => {
  try {
    localStorage.setItem(`table-view:${viewKey}`, view);
  } catch {
    // Storage blocked: the choice lasts until the page is left
  }
};

/**
 * Data table in the HEC MIS style: search + filters + export bar, white framed table with
 * zebra rows, and pagination.
 *
 * columns: [{ key, label, render?(row, position), accessor?(row), className?, exportable?, sortable?, sortValue?(row), filter? }]
 *   `render` draws the cell (position counts from 0 across pages, in the current order);
 *   `accessor` gives the plain value used by search, filters and export.
 *   Labelled columns sort on a header click; `sortValue` gives the raw value (an ISO date, a number)
 *   when the accessor formats it, and `sortable: false` opts out.
 *   `filter: true` (or a placeholder string) adds a dropdown of the column's distinct values,
 *   shown once there are at least two to choose from.
 * filterBy: extra dropdowns on fields that are not a column of their own, `[{ key, label, value?(row), placeholder? }]`.
 * defaultSort: optional starting order, `{ key, direction: "asc" | "desc" }`.
 * exportColumns: optional columns for the export only, when it needs more fields than the table shows.
 * cardView: the toolbar offers a list or card view, remembered per table under `viewKey` (default:
 *   exportName). Cards are drawn from the columns, or by `card(row)` when given.
 * Client mode (default): pass all rows, the table searches, filters, sorts and pages them itself.
 * Server mode: pass `pagination={{ page, totalPages, totalElements, pageSize, onPageChange }}`
 *   and, for search, `searchValue` + `onSearchChange`.
 */
export default function Table({
  columns,
  data = [],
  isLoading = false,
  rowKey = (row, i) => row?.id ?? i,
  onRowClick,
  searchable = true,
  searchPlaceholder = "Search",
  searchValue,
  onSearchChange,
  filters,
  actions,
  exportable = true,
  exportName = "export",
  exportColumns,
  pageSize = 10,
  pagination,
  defaultSort,
  filterBy,
  emptyTitle = "No records found",
  cardView = false,
  card: customCard,
  viewKey = exportName,
  className,
}) {
  const serverSearch = onSearchChange !== undefined;
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState(defaultSort ?? null);
  const [columnFilters, setColumnFilters] = useState({});
  const [exportOpen, setExportOpen] = useState(false);
  const card = useMemo(
    () => customCard ?? (cardView ? cardFromColumns(columns, onRowClick) : null),
    [customCard, cardView, columns, onRowClick]
  );
  const [view, setView] = useState(() => (cardView || customCard ? savedView(viewKey) : "table"));
  const exportRef = useRef(null);
  const showCards = Boolean(card) && view === "cards";

  const chooseView = (next) => {
    setView(next);
    saveView(viewKey, next);
  };

  // Close the export menu on any click outside it
  useEffect(() => {
    if (!exportOpen) return undefined;
    const close = (e) => !exportRef.current?.contains(e.target) && setExportOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [exportOpen]);

  // Column filters only make sense when the table holds every row, not one server page
  const filterColumns = useMemo(
    () =>
      pagination
        ? []
        : [
            ...columns
              .filter((c) => c.filter)
              .map((c) => ({ key: c.key, label: c.label, accessor: c.accessor, placeholder: typeof c.filter === "string" ? c.filter : undefined })),
            ...(filterBy ?? []),
          ],
    [columns, filterBy, pagination]
  );

  const filterOptions = useMemo(
    () =>
      Object.fromEntries(
        filterColumns.map((c) => {
          const values = new Set(data.map((row) => filterValueOf(row, c)).filter((v) => v !== null));
          return [c.key, [...values].sort(collator.compare).map((v) => ({ value: v, label: v }))];
        })
      ),
    [data, filterColumns]
  );

  // A selection the current data no longer contains (e.g. after switching academic year) is dropped
  const activeFilters = useMemo(
    () =>
      filterColumns.filter((c) => {
        const value = columnFilters[c.key];
        return value && filterOptions[c.key].some((o) => o.value === value);
      }),
    [filterColumns, columnFilters, filterOptions]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const searched = serverSearch || !q ? data : data.filter((row) => matches(row, columns, q));
    return activeFilters.length === 0
      ? searched
      : searched.filter((row) => activeFilters.every((c) => filterValueOf(row, c) === columnFilters[c.key]));
  }, [data, columns, query, serverSearch, activeFilters, columnFilters]);

  // Server pages arrive in the server's order, so only client rows are sorted here. Empty values always go last
  const sorted = useMemo(() => {
    const column = !pagination && sort && columns.find((c) => c.key === sort.key);
    if (!column) return filtered;
    const factor = sort.direction === "desc" ? -1 : 1;
    return filtered
      .map((row) => ({ row, value: toComparable(sortValueOf(row, column)) }))
      .sort((a, b) => (a.value === null ? (b.value === null ? 0 : 1) : b.value === null ? -1 : factor * compare(a.value, b.value)))
      .map(({ row }) => row);
  }, [filtered, sort, columns, pagination]);

  // Back to the first page whenever the client-side result set or its order changes
  useEffect(() => setPage(0), [query, data, sort, columnFilters]);

  // Ascending, then descending, then back to the starting order
  const toggleSort = (key) =>
    setSort((current) => {
      if (current?.key !== key) return { key, direction: "asc" };
      const direction = nextDirection[current.direction];
      return direction ? { key, direction } : defaultSort?.key === key ? null : defaultSort ?? null;
    });

  const hasClientFilter = !serverSearch && query.trim() !== "";
  const isFiltered = hasClientFilter || activeFilters.length > 0;
  const clearFilters = () => {
    if (!serverSearch) setQuery("");
    setColumnFilters({});
  };

  const clientPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const rows = pagination ? sorted : sorted.slice(page * pageSize, (page + 1) * pageSize);

  const exportRows = () =>
    sorted.map((row) =>
      Object.fromEntries(
        (exportColumns ?? columns)
          .filter((c) => c.exportable !== false && typeof c.label === "string")
          .map((c) => [c.label, cellValue(row, c) ?? ""])
      )
    );

  const download = (bookType) => {
    const sheet = XLSX.utils.json_to_sheet(exportRows());
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Sheet1");
    XLSX.writeFile(book, `${exportName}.${bookType}`, { bookType });
    setExportOpen(false);
  };

  const showToolbar = searchable || filters || filterColumns.length > 0 || actions || exportable || card;

  return (
    <div className={cx("flex max-w-full flex-col gap-4", className)}>
      {showToolbar && (
        <div className="flex flex-wrap items-center gap-3">
          {searchable && (
            <div className="relative w-full sm:w-72">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={serverSearch ? searchValue ?? "" : query}
                onChange={(e) => (serverSearch ? onSearchChange(e.target.value) : setQuery(e.target.value))}
                placeholder={searchPlaceholder}
                className="h-[46px] w-full rounded-lg border-2 border-slate-100 bg-white pl-11 pr-4 text-sm outline-none placeholder:text-slate-400 focus:border-brand/30 focus:ring-2 focus:ring-brand/10"
              />
            </div>
          )}
          {filters && <div className="flex flex-wrap items-center gap-3">{filters}</div>}
          {filterColumns
            .filter((c) => filterOptions[c.key].length > 1 || activeFilters.includes(c))
            .map((c) => (
              <Select
                key={c.key}
                aria-label={c.label}
                placeholder={c.placeholder ?? `Any ${c.label.toLowerCase()}`}
                value={activeFilters.includes(c) ? columnFilters[c.key] : ""}
                onChange={(e) => setColumnFilters((current) => ({ ...current, [c.key]: e.target.value }))}
                options={filterOptions[c.key]}
                className="w-full sm:w-48"
              />
            ))}
          {isFiltered && (
            <Button variant="ghost" icon={<XMarkIcon className="h-4 w-4" />} onClick={clearFilters}>
              Clear filters
            </Button>
          )}
          <div className="ml-auto flex items-center gap-3">
            {isFiltered && (
              <span className="whitespace-nowrap text-sm text-slate-500">
                {sorted.length} of {data.length}
              </span>
            )}
            {actions}
            {card && (
              <div role="group" aria-label="View" className="flex rounded-lg border-2 border-slate-100 bg-white p-0.5">
                {VIEWS.map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    title={label}
                    aria-label={label}
                    aria-pressed={view === value}
                    onClick={() => chooseView(value)}
                    className={cx(
                      "rounded-md p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30",
                      view === value ? "bg-brand/10 text-brand" : "text-slate-400 hover:text-slate-600"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                ))}
              </div>
            )}
            {exportable && (
              <div className="relative" ref={exportRef}>
                <Button variant="outline" onClick={() => setExportOpen((o) => !o)} icon={<ChevronDownIcon className="h-4 w-4" />} iconPosition="right">
                  Export
                </Button>
                {exportOpen && (
                  <div className="absolute right-0 top-full z-30 mt-1 w-40 rounded-lg border border-slate-100 bg-white py-1 shadow-lg">
                    {[
                      ["csv", "CSV"],
                      ["xlsx", "Excel"],
                    ].map(([type, label]) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => download(type)}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 text-brand" />
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showCards && !isLoading && rows.length > 0 ? (
        <InCard.Provider value>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map((row, i) => (
              <div key={rowKey(row, i)} className="min-w-0">
                {card(row, (pagination ? 0 : page * pageSize) + i)}
              </div>
            ))}
          </div>
        </InCard.Provider>
      ) : (
        <div className="thin-scrollbar overflow-x-auto rounded-lg border border-slate-100 bg-white">
          {isLoading ? (
            <Loading />
          ) : rows.length === 0 ? (
            <EmptyState title={emptyTitle} className="py-12" />
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  {columns.map((c) => {
                    const sortable = !pagination && isSortable(c);
                    const direction = sort?.key === c.key ? sort.direction : null;
                    const SortIcon = direction === "asc" ? ChevronUpIcon : direction === "desc" ? ChevronDownIcon : ChevronUpDownIcon;
                    return (
                      <th
                        key={c.key}
                        aria-sort={direction ? (direction === "asc" ? "ascending" : "descending") : undefined}
                        className={cx("whitespace-nowrap px-3 py-3 text-left font-semibold text-slate-700", c.className)}
                      >
                        {sortable ? (
                          <button type="button" onClick={() => toggleSort(c.key)} className="group inline-flex items-center gap-1 rounded hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30">
                            {c.label}
                            <SortIcon className={cx("h-3.5 w-3.5", direction ? "text-brand" : "text-slate-300 group-hover:text-slate-400")} aria-hidden="true" />
                          </button>
                        ) : (
                          c.label
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={rowKey(row, i)}
                    // Clicks on a control inside the row (a row action, a link) are not row clicks
                    onClick={onRowClick ? (e) => !e.target.closest("button, a, input, select, label") && onRowClick(row) : undefined}
                    className={cx(i % 2 === 1 && "bg-slate-50", onRowClick && "cursor-pointer hover:bg-brand/5")}
                  >
                    {columns.map((c) => (
                      <td key={c.key} className={cx("border-b border-slate-100 px-3 py-3 text-slate-700", c.className)}>
                        {c.render ? c.render(row, (pagination ? 0 : page * pageSize) + i) : cellValue(row, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {pagination ? (
        <Pagination {...pagination} />
      ) : (
        clientPages > 1 && (
          <Pagination page={page} totalPages={clientPages} onPageChange={setPage} totalElements={sorted.length} pageSize={pageSize} />
        )
      )}
    </div>
  );
}
